import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserCircle2, Loader2 } from "lucide-react";
import { hasRole } from "@/lib/roles";

/**
 * Chantier 3 C9 (2026-09-06) — Dropdown "Assigné à" du QuoteEditor.
 *
 * Admin only. Un commercial voit son propre nom en lecture seule (le badge
 * QuoteAssignedBadge s'occupe de ce cas). Le composant :
 *   - fetch `/team/members` une seule fois au mount
 *   - filtre les rôles autorisés (admin + commercial)
 *   - PATCH `/quotes/{id}/assign` sur onChange, idempotent côté backend
 *   - synchronise l'état parent via `onChange(newUserId)` (facultatif)
 */
export default function AssignSelector({
  quoteId,
  currentUserId,
  onChange,
  disabled = false,
  className = "",
}) {
  const [members, setMembers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(currentUserId || "none");

  useEffect(() => {
    setValue(currentUserId || "none");
  }, [currentUserId]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/team/members")
      .then(({ data }) => {
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : data?.items || [];
        // Seuls admin + commercial peuvent recevoir un devis assigné (garde back A3)
        // BLOC 19/10 — Exclure users désactivés (is_active=false). Les devis
        // déjà assignés gardent le nom du désactivé (traçabilité), mais on ne
        // propose plus de nouvelle assignation.
        setMembers(
          arr.filter((m) => hasRole(m, ["admin", "commercial"]) && m.is_active !== false),
        );
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = async (next) => {
    const targetUserId = next === "none" ? null : next;
    const previous = value;
    setValue(next);
    setSaving(true);
    try {
      const { data } = await api.patch(`/quotes/${quoteId}/assign`, {
        user_id: targetUserId,
      });
      if (typeof onChange === "function") onChange(data?.assigned_to ?? null);
      toast.success(targetUserId ? "Devis assigné" : "Devis désassigné");
    } catch (e) {
      setValue(previous);
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="quote-assign-selector">
      <UserCircle2 className="h-4 w-4 text-slate-400" aria-hidden />
      <Select
        value={value}
        onValueChange={handleChange}
        disabled={disabled || saving || members === null}
      >
        <SelectTrigger
          className="h-8 min-w-[180px] border-slate-700 bg-slate-950/40 text-sm"
          data-testid="quote-assign-trigger"
        >
          <SelectValue placeholder="Assigner à…" />
        </SelectTrigger>
        <SelectContent className="border-slate-700 bg-slate-900">
          <SelectItem value="none" data-testid="quote-assign-option-none">
            Non assigné
          </SelectItem>
          {(members || []).map((m) => (
            <SelectItem
              key={m.id}
              value={m.id}
              data-testid={`quote-assign-option-${m.id}`}
            >
              {m.first_name} {m.last_name}
              {hasRole(m, ["admin"]) ? " (admin)" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" aria-hidden />}
    </div>
  );
}
